import React, { useState, useRef } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useSnackbar } from 'notistack';
import { Switch, Route, useHistory, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import UserForm from '../../components/UserForm';
import UsersOverview from './UsersOverview';
import UsersInvites from './UsersInvites/UsersInvites';
import UsersProvider from './UsersProvider';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Page from '@afosto/app-components-react/Page';
import { users as usersApi } from '@afosto/iam/api/Users';
import translations from './translations';

const Users = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:users:read']);

  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const container = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [refreshDataGrid, setRefreshDataGrid] = useState(false);

  /**
   * Handle the onSubmit event of the user form in the create new user drawer.
   * @param {Array} roles
   * @param {Object} userData
   * @returns {Promise}
   */
  const handleCreateUser = async ({ roles, firstName, lastName, email }) => {
    try {
      const userResponse = await usersApi.createUser({
        first_name: firstName,
        last_name: lastName,
        email,
      });
      const { data: { id } = {} } = userResponse;
      const rolesResponse = await usersApi.updateUserRoles(
        id,
        roles.map(role => role.value),
      );
      const { data: user = {} } = rolesResponse;

      await usersApi.requestToken({
        email: user.email,
        type: 'invite',
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.userCreated), {
        variant: 'success',
      });
      setShowCreateUser(false);

      if (location.pathname === '/users/invites') {
        setRefreshDataGrid(true);
      } else {
        history.push('/users/invites');
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.userNotCreated), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <UsersProvider value={{ refreshDataGrid, setRefreshDataGrid }}>
      <Grid container wrap="nowrap">
        <Page
          title={intl.formatMessage(translations.title)}
          style={{ flex: 1 }}
          fullWidth
          primaryAction={
            hasPermissions(['iam:users:write'])
              ? {
                  text: intl.formatMessage(translations.user.create.button),
                  action: () => {
                    setShowCreateUser(true);
                  },
                }
              : null
          }
          subNavigation={{
            current: location.pathname,
            tabs: [
              {
                label: intl.formatMessage(translations.subNavigation.overview),
                path: '/users',
              },
              {
                label: intl.formatMessage(translations.subNavigation.invites),
                path: '/users/invites',
              },
            ],
            onChange: (e, value) => history.replace(value),
          }}
        >
          <Switch>
            <Route path="/users/invites" component={UsersInvites} />
            <Route
              path="/users"
              render={props => <UsersOverview {...props} container={container} />}
            />
          </Switch>
        </Page>
        <div ref={container} />
        <FormDrawer
          title={intl.formatMessage(translations.user.create.title)}
          isOpen={showCreateUser}
          onClose={() => setShowCreateUser(false)}
          drawerWidth="400px"
        >
          <UserForm onSubmit={handleCreateUser} onCancel={() => setShowCreateUser(false)} />
        </FormDrawer>
      </Grid>
    </UsersProvider>
  );
};

export default Users;
